import { DynamicDrawUsage, InstancedMesh, Mesh, Object3D, Vector3 } from 'three';
import { LAYER } from '../core/Layers.js';
import { frame } from '../core/FrameUniforms.js';
import { settings } from '../config/settings.js';
import { getColor } from '../utils/color.js';
import {
  createConstructMaterial,
  syncConstructMaterial
} from '../materials/ConstructMaterial.js';

const _instance = new Object3D();
const _up = new Vector3(0, 1, 0);
const MIN_TIMELINE_DIVISOR = 1e-4;

/**
 * Convert real impact-phase seconds back to the ability's authored timeline.
 *
 * `Ability#impactDuration` applies the global lifetime multiplier. Milestones
 * must therefore read this logical clock or a short/long global lifetime would
 * respectively skip the finale or leave the choreography waiting in real time.
 */
export function logicalImpactTime(impactTime) {
  const lifetimeScale = Math.max(MIN_TIMELINE_DIVISOR, settings.global.lifetime);
  return impactTime / lifetimeScale;
}

/** Protect a division without imposing a visible minimum duration. */
export function safeTimelineDuration(duration) {
  return Math.max(MIN_TIMELINE_DIVISOR, duration);
}

/** Create one construct mesh or one instanced construct batch. */
export function createConstruct(geometry, style, { count = 0, normal = false, renderOrder = 12 } = {}) {
  const material = createConstructMaterial(style, { normal });
  const mesh = count > 0
    ? new InstancedMesh(geometry, material, count)
    : new Mesh(geometry, material);
  mesh.layers.set(LAYER.VFX);
  mesh.renderOrder = renderOrder;
  mesh.frustumCulled = false;
  if (mesh.isInstancedMesh) {
    mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    mesh.count = 0;
  }
  return { mesh, material };
}

/** Pose one slot in an InstancedMesh without allocating temporary transforms. */
export function setConstructInstance(mesh, index, position, scale, rotation = null) {
  _instance.position.copy(position);
  if (typeof scale === 'number') _instance.scale.setScalar(scale);
  else _instance.scale.copy(scale);
  if (rotation) _instance.rotation.set(rotation.x, rotation.y, rotation.z);
  else _instance.rotation.set(0, 0, 0);
  _instance.updateMatrix();
  mesh.setMatrixAt(index, _instance.matrix);
}

export function commitConstructInstances(mesh, count) {
  mesh.count = Math.max(0, Math.min(mesh.instanceMatrix.count, count));
  mesh.instanceMatrix.needsUpdate = true;
  mesh.visible = mesh.count > 0;
}

export function syncConstruct(entry, config, state) {
  syncConstructMaterial(entry.material, config, state);
}

/** Apply the common four-stop palette used by the new spell particle systems. */
export function syncParticlePalette(system, config) {
  system.setGradient(
    getColor(config.colorC),
    getColor(config.colorB),
    getColor(config.colorA),
    getColor(config.colorD || config.colorA)
  );
}

/** Fill and emit through a caller-owned scratch object. */
export function emitParticles(system, scratch, {
  position,
  direction = _up,
  count,
  radius = 0,
  speed = 1,
  speedVariance = 0.35,
  spread = 0.5,
  size = 0.08,
  sizeVariance = 0.5,
  life = 0.7,
  lifeVariance = 0.3,
  spin = 0,
  anchor = null,
  inherit = null,
  tint = null
}) {
  scratch.position = position;
  scratch.radius = radius;
  scratch.direction = direction;
  scratch.speed = speed * settings.global.particleSpeed;
  scratch.speedVariance = speedVariance;
  scratch.spread = spread;
  scratch.inherit = inherit;
  scratch.anchor = anchor;
  scratch.size = size * settings.global.particleSize;
  scratch.sizeVariance = sizeVariance;
  scratch.life = life * settings.global.particleLifetime;
  scratch.lifeVariance = lifeVariance;
  scratch.spin = spin;
  scratch.tint = tint;
  scratch.time = frame.uTime.value;
  system.emit(Math.round(count * settings.global.particleCount), scratch);
}

/** t is 0..1 during impact and 1..2 during the base fade phase. */
export function lifecycleFade(t) {
  return t <= 1 ? 1 : Math.max(0, 2 - t);
}

export function disposeConstruct(entry) {
  entry.mesh.dispose?.();
  entry.material.dispose();
  entry.mesh.parent?.remove(entry.mesh);
}
